import styled, { css } from 'styled-components';
import { down } from 'styled-breakpoints';
import { Link } from 'react-router-dom';

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.125rem;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  color: var(--colors-brand-primary);

  > h2 {
    font-size: 1rem;
    font-weight: 700;
    color: var(--colors-foreground-100);
  }
`;

export const CountBadge = styled.span`
  ${({ theme }) => css`
    font-size: 0.625rem;
    font-weight: 600;
    background: ${theme.colors.brand.primary.opaque};
    color: ${theme.colors.brand.primary.default};
    padding: 0.1875rem 0.5rem;
    border-radius: 1.25rem;
  `}
`;

export const ShowAllLink = styled(Link)`
  ${({ theme }) => css`
    font-size: 0.8125rem;
    color: ${theme.colors.brand.primary.default};
    font-weight: 600;
  `}
`;

export const SpeakersLayout = styled.div`
  display: flex;
  gap: 1rem;
  align-items: stretch;

  ${down('lg')} {
    flex-direction: column;
  }
`;

export const FeaturedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;

  ${down('lg')} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${down('md')} {
    grid-template-columns: 1fr;
  }
`;

export const GridCard = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.background[100]};
    border-radius: 1rem;
    border: 1px solid ${theme.colors.background[40]};
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
    }
  `}
`;

export const GridCardPhoto = styled.div`
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;

  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.4s ease;
  }

  ${GridCard}:hover > & > img {
    transform: scale(1.03);
  }
`;

export const GridCardInfo = styled.div`
  padding: 1rem 1.125rem;
  text-align: center;
`;

export const GridCardName = styled.div`
  ${({ theme }) => css`
    font-size: 0.9375rem;
    font-weight: 700;
    color: ${theme.colors.foreground[100]};
    margin-bottom: 0.1875rem;
  `}
`;

export const GridCardRole = styled.div`
  font-size: 0.75rem;
  color: var(--colors-foreground-60);
`;

export const CompanyLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2rem;
  margin-top: 0.5rem;

  > img {
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
  }
`;

export const FeaturedCard = styled.div`
  ${({ theme }) => css`
    width: 18.125rem;
    flex-shrink: 0;

    ${FeaturedGrid} > & {
      width: 100%;
    }
    background: ${theme.colors.background[100]};
    border-radius: 1rem;
    border: 1px solid ${theme.colors.background[40]};
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2.75rem 1.5rem 2.5rem;
    text-align: center;
    position: relative;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
    }

    ${down('lg')} {
      width: 100%;
    }
  `}
`;

export const FeaturedBg = styled.div`
  ${({ theme }) => css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5rem;
    background: linear-gradient(
      135deg,
      ${theme.colors.brand.primary.opaque},
      transparent
    );
  `}
`;

export const AvatarWrapper = styled.div`
  position: relative;
  margin-bottom: 0.875rem;

  > img {
    width: 6.5rem;
    height: 6.5rem;
    border-radius: 0.75rem;
    object-fit: cover;
    display: block;
  }
`;

export const FeaturedName = styled.div`
  ${({ theme }) => css`
    font-size: 1rem;
    font-weight: 700;
    color: ${theme.colors.foreground[100]};
    margin-bottom: 0.125rem;
  `}
`;

export const FeaturedRole = styled.div`
  font-size: 0.75rem;
  color: var(--colors-foreground-60);
  margin-bottom: 0.0625rem;
`;

export const FeaturedCompany = styled.div`
  font-size: 0.6875rem;
  color: var(--colors-foreground-40);
  margin-bottom: 0.875rem;
`;

export const TopicQuote = styled.div`
  ${({ theme }) => css`
    width: 100%;
    background: ${theme.colors.background[80]};
    border-radius: 0.625rem;
    padding: 0.625rem 0.875rem;
    margin-bottom: 0.875rem;
    text-align: left;

    > span {
      font-size: 0.75rem;
      font-weight: 600;
      color: ${theme.colors.foreground[100]};
      line-height: 1.5;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  `}
`;

export const FeaturedTime = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.6875rem;
    color: ${theme.colors.foreground[40]};
    font-weight: 500;
  `}
`;

export const OthersGridWrapper = styled.div`
  flex: 1;
  position: relative;
`;

export const OthersGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.625rem;
  align-content: start;

  ${down('lg')} {
    grid-template-columns: 1fr;
  }
`;

export const OthersGridFade = styled.div`
  ${({ theme }) => css`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 5rem;
    background: linear-gradient(transparent, ${theme.colors.background[100]});
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding-bottom: 0;
    pointer-events: none;

    > * {
      pointer-events: auto;
      transform: translateY(1.25rem);
    }
  `}
`;

export const CompactCard = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.background[100]};
    border-radius: 0.75rem;
    border: 1px solid ${theme.colors.background[40]};
    padding: 0.875rem;
    display: flex;
    gap: 0.6875rem;
    align-items: center;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
    }
  `}
`;

export const CompactAvatar = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 0.625rem;
  flex-shrink: 0;
  overflow: hidden;

  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const CompactInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const CompactNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.125rem;

  > span:first-child {
    font-size: 0.875rem;
    font-weight: 600;
  }
`;

export const CompactRole = styled.div`
  font-size: 0.6875rem;
  color: var(--colors-foreground-60);
  margin-bottom: 0.1875rem;
`;

export const CompactTime = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.625rem;
    color: ${theme.colors.foreground[40]};
  `}
`;

export const ShowMoreButton = styled(Link)`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background: ${theme.colors.background[100]};
    border: 1px solid ${theme.colors.background[40]};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    grid-column: 1 / -1;
    margin: -0.5rem auto 0;
    color: ${theme.colors.foreground[60]};
    cursor: pointer;
    transition: all 0.2s ease;
    text-decoration: none;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      color: ${theme.colors.brand.primary.default};
    }
  `}
`;
